import React, { useCallback, useState } from "react";
import _ from "lodash";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Transfer, Checkbox, Space } from "antd";
import { MenuOutlined } from "@ant-design/icons";
// ------------------------
// Defaults
// ------------------------
const DEFAULT_COLUMNS = [
  {
    dataIndex: "title",
    title: "Name",
  },
];
const defautFilterOption = (inputValue, item) =>
  _.toLower(item.title).indexOf(_.toLower(inputValue)) !== -1;
// ------------------------
// Draggable Item (internal)
// ------------------------
const DraggableVisibilityDropdownItem = ({
  id,
  disabled,
  checked,
  title,
  onCheckUnCheck,
  className,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
    cursor: disabled ? "not-allowed" : "grab",
    padding: "4px 0",
    background: isDragging ? "#fafafa" : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={className}
    >
      <Space>
        <MenuOutlined {...listeners} style={{ fontSize: 18, paddingLeft: 6 }} />
        <Checkbox className="w-100" checked={checked} onChange={onCheckUnCheck}>
          {title}
        </Checkbox>
      </Space>
    </div>
  );
};
// ------------------------
// Main Transfer Component
// ------------------------
const TransferComponent = ({
  leftColumns = DEFAULT_COLUMNS,
  rightColumns = DEFAULT_COLUMNS,
  makeDraggableSort = false,
  disableDND = false,
  scrollY = 200,
  showSearch = true,
  filterOption = defautFilterOption,
  value,
  onChange,
  itemRender = (item) => item.title,
  ...restProps
}) => {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      // When the drag ends, clear the active item's ID to hide the overlay
      setActiveId(null);
      if (!over || active.id === over.id) return;
      const oldIndex = value.findIndex((id) => id === active.id);
      const newIndex = value.findIndex((id) => id === over.id);
      const newOrder = arrayMove(value, oldIndex, newIndex);
      onChange(newOrder);
    },
    [value, onChange]
  );
  return (
    <Transfer
      listStyle={{ height: 300 }}
      targetKeys={value}
      showSearch={showSearch}
      filterOption={filterOption}
      onChange={onChange}
      render={itemRender}
      {...restProps}
    >
      {({ direction, filteredItems, onItemSelect, selectedKeys }) => {
        if (direction === "right" && makeDraggableSort) {
          return (
            <div style={{ height: scrollY, overflow: "auto" }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                // When a drag starts, we capture the active item's ID in our state.
                // This tells the DragOverlay what to render.
                onDragStart={({ active }) => setActiveId(active.id)}
              >
                <SortableContext
                  items={filteredItems.map((item) => item.key)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredItems.map((entry) => (
                    <DraggableVisibilityDropdownItem
                      key={entry.key}
                      id={entry.key}
                      disabled={disableDND}
                      checked={selectedKeys.includes(entry.key)}
                      title={entry.title}
                      className="p-y-xs"
                      onCheckUnCheck={(e) =>
                        onItemSelect(entry.key, e.target.checked)
                      }
                    />
                  ))}
                </SortableContext>
                <DragOverlay>
                  {activeId && (
                    <DraggableVisibilityDropdownItem
                      id={activeId}
                      title={filteredItems.find(item => item.key === activeId)?.title || activeId }
                      checked={selectedKeys.includes(activeId)}
                      disabled={disableDND}
                      onCheckUnCheck={() => {}}
                    />
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          );
        }
      }}
    </Transfer>
  );
};
export default TransferComponent;